import { useMemo } from "react";

import Feedback from "ds/components/Feedback";
import useTypedContext from "hooks/useTypedContext";
import { StackSettingsContextData } from "views/Stack/Settings/Context";
import FeedbackActions from "ds/components/Feedback/Actions";
import Button from "ds/components/Button";
import { ArrowCircle } from "components/icons/generated";
import { VENDOR_CONFIG_TYPENAME, VENDOR_KEY } from "constants/vendor";
import { TerraformWorkflowTool } from "types/generated";

import { showStackSettingsTerragruntMigrationConfirmationModal } from "./ConfirmationModal";
import { showStackSettingsTerragruntRollbackConfirmationModal } from "./RollbackConfirmationModal";

type StackSettingsTerragruntMigrationProps = {
  vendorKey?: VENDOR_KEY;
};

const StackSettingsTerragruntMigration = ({ vendorKey }: StackSettingsTerragruntMigrationProps) => {
  const { stackSettings } = useTypedContext(StackSettingsContextData);
  const canBeMigrated = useMemo(() => {
    const vendorConfig = stackSettings.vendorConfig;
    return (
      vendorKey === VENDOR_KEY.TERRAFORM &&
      vendorConfig?.__typename === VENDOR_CONFIG_TYPENAME.TERRAFORM &&
      vendorConfig.workflowTool !== TerraformWorkflowTool.Custom &&
      stackSettings.labels?.some((label) => label === "terragrunt")
    );
  }, [stackSettings.labels, stackSettings.vendorConfig, vendorKey]);

  const canRollbackMigration = useMemo(
    () => vendorKey === VENDOR_KEY.TERRAGRUNT && stackSettings.wasMigratedFromTerraform,
    [stackSettings, vendorKey]
  );

  const handleMigrateToTerragrunt = () => {
    showStackSettingsTerragruntMigrationConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  const handleRollbackToTerraform = () => {
    showStackSettingsTerragruntRollbackConfirmationModal({
      stackId: stackSettings.id,
    });
  };

  if (canBeMigrated) {
    return (
      <Feedback type="callout" variant="warning" title="Terragrunt via labels will be deprecated">
        This stack uses Terragrunt through labels on a Terraform stack. We recommend migrating to a
        native Terragrunt stack for better support and future compatibility.
        <FeedbackActions>
          <Button variant="contrast" size="small" onPress={handleMigrateToTerragrunt}>
            Migrate to Terragrunt
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  if (canRollbackMigration) {
    return (
      <Feedback type="callout" variant="info">
        If you're having issues with Terragrunt migration, you can roll it back to Terraform.
        <FeedbackActions>
          <Button
            variant="secondary"
            size="small"
            startIcon={ArrowCircle}
            onPress={handleRollbackToTerraform}
          >
            Rollback to Terraform
          </Button>
        </FeedbackActions>
      </Feedback>
    );
  }

  return null;
};

export default StackSettingsTerragruntMigration;
